<script setup lang="ts">
import { ref, provide, watch } from 'vue';
import { RouterView } from 'vue-router';
import HeaderBar from '@/components/HeaderBar.vue';
import CustomAlerts from '@/components/CustomAlerts.vue';
import { ManifestHandler } from '@/viewerExtensions';
import * as Sentry from '@sentry/vue';
import type { Viewer, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestTranslation } from '@/types/IManifest';
import type { INotice } from '@/types/INotice';

const viewer = ref<Nullable<Viewer>>();
const viewerExtensions = ref<Record<string, unknown>>();
const manifest = ref<IManifest>(ManifestHandler.generateEmptyManifest());
const notices = ref<INotice[]>([]);
const enableDragDropFileUpload = ref(true);
const translationUpdateDebounce = ref<number>();
const translationLanguages = ref<{ [key: string]: Nullable<string> }>({
  English: null,
  Spanish: 'es',
  French: 'fr',
  German: 'de',
  Korean: 'ko',
  Japanese: 'ja',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Simplified Chinese': 'zh',
  Taiwan: 'zt',
});
const glbStorage = ref(new Map<string, string>());

const manifestWatcher = (newValue: IManifest) => {
  if (translationUpdateDebounce.value) clearTimeout(translationUpdateDebounce.value);

  translationUpdateDebounce.value = window.setTimeout(() => {
    const { hotspots, ctaButtons, name } = newValue;
    const hotspotNames = Object.keys(hotspots);
    const ctaButtonValues = ctaButtons.map(({ text }) => text);
    const translations = [...hotspotNames, ...ctaButtonValues, name].filter(Boolean);
    const defaultTranslations = Object.values(translationLanguages.value).reduce((output, language) => {
      if (!language) return output;

      output[language as keyof IManifestTranslation] = '';

      return output;
    }, {} as IManifestTranslation);

    for (const translation of translations) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (manifest.value.translations[translation]) continue;

      manifest.value.translations[translation] = defaultTranslations;
    }

    window.manifest = newValue;
  }, 350);

  Sentry.getCurrentScope().clearAttachments().addAttachment({
    filename: 'manifest.json',
    data: JSON.stringify(newValue, null, 2),
  });
};

provide('viewer', viewer);
provide('viewerExtensions', viewerExtensions);
provide('manifest', manifest);
provide('translationLanguages', translationLanguages);
provide('notices', notices);
provide('enableDragDropFileUpload', enableDragDropFileUpload);
provide('glbStorage', glbStorage);

watch(manifest, manifestWatcher, { deep: true });
</script>

<template>
  <div class="h-full flex flex-col select-none">
    <HeaderBar class="bg-white z-20" />
    <RouterView />
    <CustomAlerts v-once />
  </div>
</template>

<style>
@import '@/assets/styles/base.css';
</style>
